/**
 * CoPilot Beta badge
 *
 * A small "Beta" pill shown next to the Tonnie CoPilot title while the
 * assistant is in beta. Hovering (or keyboard-focusing) reveals a short
 * explainer that invites the user to rate replies with the existing per-message
 * 👍/👎 feedback so we can keep improving it — Human-in-the-Loop, no new
 * feedback surface.
 *
 * The host app decides whether to render this at all via the
 * `ENABLE_COPILOT_BETA` feature flag, so the whole beta layer drops cleanly in
 * a future build by flipping one flag.
 */

import { useTranslation } from '@tonnie/locale';
import { HoverCard, HoverCardContent, HoverCardTrigger, cn } from '@tonnie/ui';

interface ICoPilotBetaBadgeProps {
  className?: string;
}

export function CoPilotBetaBadge({ className }: ICoPilotBetaBadgeProps) {
  const { t } = useTranslation();

  return (
    <HoverCard openDelay={100} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label={t('copilot.beta.ariaLabel')}
          data-testid="copilot-beta-badge"
          className={cn(
            'inline-flex h-4 items-center rounded-full border border-primary/25 bg-primary/15 px-1.5',
            'text-2xs font-semibold uppercase leading-none tracking-wide text-primary-accessible',
            'cursor-help transition-colors hover:bg-primary/25',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            className
          )}
        >
          {t('copilot.beta.badge')}
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="bottom" align="start" className="w-64">
        <p className="text-xs font-semibold text-foreground">{t('copilot.beta.tooltipTitle')}</p>
        <p className="mt-1 text-xs leading-snug text-muted-foreground">
          {t('copilot.beta.tooltipBody')}
        </p>
      </HoverCardContent>
    </HoverCard>
  );
}
